// src/pages/auth/Login.jsx
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Form, Input, Button, notification, Select } from "antd";
import {
  MailOutlined,
  LockOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setCredentials } from "@/store/auth/authSlice";
import logoText from "@/assets/images/unzippd-wordmark-white-v7lIpH0R.png";
import logoIcon from "@/assets/images/unzippd-logo.png";
import {
  useAdminLoginMutation,
  useLoginMutation,
} from "../../store/api/apiSlice";
import ForgotPasswordModals from "./ForgotPasswordModals";
import OTPVerificationModal from "../../components/Modals/OTPVerificationModal";
import {
  getStableDeviceFingerprint,
  storeTrustedDeviceFingerprint,
} from "../../utils";
import {
  buildUserDataFromLoginResponse,
  buildUserDataFromVerified,
  navigateByUserType,
} from "../../utils/auth";
import { extractAccessTokenFromLoginPayload } from "../../utils/extractAccessToken";
import {
  otpConsoleHintOnce,
  otpDebugLog,
  otpProblemLog,
} from "../../utils/otpDebugLog";
import { SOCIAL_LINKS } from "../../constants/landing";

const { Option } = Select;
const CURRENT_YEAR = new Date().getFullYear();

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [login, { isLoading: isUserLoading }] = useLoginMutation();
  const [adminLogin, { isLoading: isAdminLoading }] = useAdminLoginMutation();

  const [forgotPasswordVisible, setForgotPasswordVisible] = useState(false);
  const [otpModalVisible, setOtpModalVisible] = useState(false);
  const [pendingLoginData, setPendingLoginData] = useState(null);

  const setAuthSession = ({ user, token, role, userData }) => {
    dispatch(
      setCredentials({
        user,
        token,
        role,
        userData,
      }),
    );
  };

  useEffect(() => {
    const metaTag = document.createElement("meta");
    metaTag.name = "viewport";
    metaTag.content =
      "width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no";

    document.getElementsByTagName("head")[0].appendChild(metaTag);

    return () => {
      document.getElementsByTagName("head")[0].removeChild(metaTag);
    };
  }, []);

  const onFinish = async (values) => {
    try {
      const { userType, ...credentials } = values;

      let response;

      credentials.type = userType;

      let deviceFingerprint = null;

      try {
        deviceFingerprint = await getStableDeviceFingerprint();

        credentials.device_fingerprint = deviceFingerprint.hash;

        if (deviceFingerprint.fingerprint) {
          credentials.device_data = deviceFingerprint.fingerprint;
        }

        otpDebugLog("login.fingerprint.attached", {
          userType,
          fromTrustedStorage: Boolean(deviceFingerprint.fromTrustedStorage),
          device_fingerprint: credentials.device_fingerprint,
          hasDeviceData: Boolean(credentials.device_data),
        });
      } catch (error) {
        credentials.device_fingerprint = "unknown";
        otpDebugLog("login.fingerprint.error", {
          message: error?.message,
        });
      }

      if (userType === "admin") {
        response = await adminLogin(credentials).unwrap();
      } else {
        response = await login(credentials).unwrap();
      }

      const hasAccessToken =
        !!response?.data?.access_token || !!response?.access_token;
      const otpRequiredByFlags =
        response?.data?.requires_otp === true ||
        response?.data?.needs_otp === true;
      const otpRequiredByMessage =
        /otp sent successfully/i.test(response?.message || "") ||
        /otp verification required/i.test(response?.message || "");
      const otpRequired =
        !hasAccessToken && (otpRequiredByFlags || otpRequiredByMessage);

      otpDebugLog("login.response", {
        userType,
        hasAccessToken,
        otpRequired,
        requiresOtp: response?.data?.requires_otp,
        needsOtp: response?.data?.needs_otp,
        messagePreview: String(response?.message || "").slice(0, 120),
        dataKeys:
          response?.data && typeof response.data === "object"
            ? Object.keys(response.data)
            : [],
      });

      if (otpRequired) {
        const otpNotificationKey = "otp-sent-success";

        otpConsoleHintOnce();

        setPendingLoginData({
          userType,
          email: credentials.email,
          response: response.data,
          deviceFingerprintSnapshot:
            deviceFingerprint && deviceFingerprint.hash
              ? {
                  hash: deviceFingerprint.hash,
                  fingerprint: deviceFingerprint.fingerprint,
                }
              : null,
        });

        notification.success({
          key: otpNotificationKey,
          message: "OTP Sent Successfully",
          description:
            response.data?.message ||
            "Please check your email for the verification code.",
          duration: 1.5,
          onClose: () => {
            setOtpModalVisible(true);
          },
        });

        otpDebugLog("login.otp_modal.opening", {
          hasDeviceSnapshot: Boolean(
            deviceFingerprint && deviceFingerprint.hash,
          ),
        });

        return;
      }

      if (userType === "admin" || userType === response?.data?.type) {
        const accessToken = extractAccessTokenFromLoginPayload(response);

        if (!accessToken) {
          notification.error({
            message: "Login Failed",
            description:
              "No access token returned. Check the API response shape.",
          });

          return;
        }

        if (
          deviceFingerprint?.hash &&
          deviceFingerprint.hash !== "unknown"
        ) {
          storeTrustedDeviceFingerprint(
            {
              hash: deviceFingerprint.hash,
              fingerprint: deviceFingerprint.fingerprint,
            },
            30,
          );
        }

        setAuthSession({
          user: response?.data,
          token: accessToken,
          role: response?.data?.type ?? response?.type ?? userType,
          userData: buildUserDataFromLoginResponse(response, userType),
        });

        navigateByUserType(navigate, userType);
      }
    } catch (error) {
      otpDebugLog("login.error", {
        status: error?.status,
        message: error?.data?.message || error?.message,
        data: error?.data,
      });
      otpProblemLog("login.error", {
        status: error?.status,
        message: error?.data?.message || error?.message,
        data: error?.data,
      });
      if (!error?.data?.message) {
        notification.error({
          message: "Login Failed",
          description: "Something went wrong. Please try again.",
        });
      }
    }
  };

  const handleForgotPassword = (e) => {
    e.preventDefault();
    setForgotPasswordVisible(true);
  };

  const handleForgotPasswordCancel = () => {
    setForgotPasswordVisible(false);
  };

  const handleOTPSuccess = (verifiedData) => {
    const { userType, email } = pendingLoginData;

    const accessToken = extractAccessTokenFromLoginPayload(verifiedData);

    otpDebugLog("login.otp_success", {
      userType,
      hasAccessToken: Boolean(accessToken),
      payloadKeys:
        verifiedData && typeof verifiedData === "object"
          ? Object.keys(verifiedData)
          : [],
    });

    if (!accessToken) {
      otpDebugLog("login.otp_success.no_token", {
        verifiedPreview:
          typeof verifiedData === "object"
            ? JSON.stringify(verifiedData).slice(0, 400)
            : verifiedData,
      });
      otpProblemLog("login.otp_success.no_token", {
        payloadKeys:
          verifiedData && typeof verifiedData === "object"
            ? Object.keys(verifiedData)
            : [],
      });
      notification.error({
        message: "Verification Failed",
        description: "No access token returned after OTP verification.",
      });

      return;
    }

    setAuthSession({
      user: verifiedData,
      token: accessToken,
      role: verifiedData?.data?.type ?? verifiedData?.type ?? userType,
      userData: buildUserDataFromVerified(verifiedData, userType, email),
    });

    navigateByUserType(navigate, userType);
  };

  const handleOTPCancel = () => {
    setOtpModalVisible(false);
    setPendingLoginData(null);
  };

  return (
    <div className="min-h-[100dvh] min-h-screen flex bg-[#222222]">
      <div className="w-full mx-auto lg:w-[45%] lg:max-w-2xl px-4 py-3 lg:px-5 lg:py-4 flex flex-col">
        <div className="w-full max-w-md mx-auto flex flex-col flex-1 min-h-0">
          <div className="mb-4 mt-4 flex justify-center">
            <Link
              to="/"
              aria-label="Unzippd home"
              className="inline-flex items-center gap-3 no-underline"
            >
              <img
                src={logoIcon}
                alt="Unzippd logo"
                className="w-8 h-8 inline-block"
              />
              <img
                src={logoText}
                alt="Unzippd word mark"
                className="w-[8rem] inline-block"
              />
            </Link>
          </div>
          <div className="mb-4 text-center">
            <h1 className="text-[26px] sm:text-[28px] font-bold text-white mb-1.5">
              Welcome Back!
            </h1>
            <p className="text-gray-300 font-light text-sm sm:text-base leading-snug">
              Sign in to your account to start using Unzippd. Enter your email
              address and password in the fields below.
            </p>
          </div>
          <Form
            name="login"
            onFinish={onFinish}
            layout="vertical"
            requiredMark={false}
            className="w-full [&_.ant-form-item-label>label]:!text-gray-200"
          >
            <Form.Item
              name="userType"
              rules={[
                {
                  required: true,
                  message: "Please select user type!",
                },
              ]}
              className="mb-2"
              label="Select User"
            >
              <Select
                placeholder="Select User Type"
                className="h-12 rounded-2xl"
              >
                <Option value="admin">Admin</Option>
                <Option value="organization">Organisation</Option>
                <Option value="interpreter">Interpreter</Option>
              </Select>
            </Form.Item>
            <Form.Item
              label="Email Address"
              name="email"
              rules={[
                {
                  required: true,
                  message: "Please input your email!",
                },
                {
                  type: "email",
                  message: "Please enter a valid email!",
                },
              ]}
              className="mb-2"
            >
              <Input
                prefix={<MailOutlined className="text-black" />}
                placeholder="Please enter your email address here..."
                className="h-12 rounded-2xl"
                style={{ fontSize: "16px" }}
              />
            </Form.Item>
            <Form.Item
              label="Password"
              name="password"
              rules={[
                {
                  required: true,
                  message: "Please input your password!",
                },
              ]}
              className="mb-3"
            >
              <Input.Password
                prefix={<LockOutlined className="text-black" />}
                placeholder="Minimum 8 characters..."
                className="h-12 rounded-2xl"
                style={{ fontSize: "16px" }}
                iconRender={(visible) =>
                  visible ? (
                    <EyeOutlined className="text-black" />
                  ) : (
                    <EyeInvisibleOutlined className="text-black" />
                  )
                }
              />
            </Form.Item>
            <div className="flex justify-between items-center mb-4">
              <a
                href="#"
                onClick={handleForgotPassword}
                className="text-gray-200 hover:text-white"
              >
                Forgot Password?
              </a>
            </div>
            <div className="w-full flex justify-center">
              <Button
                type="primary"
                htmlType="submit"
                className="h-12 rounded-2xl font-medium text-base w-full sm:w-[180px] bg-white !text-[#202835] border-none hover:!bg-gray-100"
                loading={isUserLoading || isAdminLoading}
              >
                {!isUserLoading && !isAdminLoading && "Login Account"}
              </Button>
            </div>
          </Form>
          <div className="mt-auto pt-4 sm:pt-5 text-center">
            <div className="flex items-center justify-center gap-4">
              <div className="flex items-center justify-center gap-2">
                {SOCIAL_LINKS.map(({ href, icon: Icon, label }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    aria-label={label}
                    title={label}
                    className="text-gray-300 hover:text-white"
                  >
                    <Icon className="w-5 h-5 md:w-5 md:h-5 rounded-[6px]" />
                  </a>
                ))}
              </div>
            </div>
            <p className="text-gray-400 text-xs sm:text-sm mt-3 text-center">
              © {CURRENT_YEAR} Unzippd. All rights reserved.
            </p>
          </div>
        </div>
      </div>
      <ForgotPasswordModals
        visible={forgotPasswordVisible}
        onCancel={handleForgotPasswordCancel}
      />
      <OTPVerificationModal
        visible={otpModalVisible}
        onCancel={handleOTPCancel}
        onSuccess={handleOTPSuccess}
        email={pendingLoginData?.email}
        userType={pendingLoginData?.userType}
        loginDeviceFingerprint={pendingLoginData?.deviceFingerprintSnapshot}
      />
    </div>
  );
};

export default Login;
